"use client";

import Image from "next/image";
// CMS-supplied hrefs are emitted verbatim - do NOT swap this for @repo/ui/navigation.
// The CMS localizes its own URLs (e.g. "cl/es/centro-de-ayuda/") and also returns
// external/relative ones, so prefixing here would double or corrupt the locale.
import Link from "next/link";

import { ChevronRight } from "lucide-react";

import { Card, CardContent, CardFooter } from "@repo/ui/components/ui/card";
import type { CardBlock as CardBlockType } from "@repo/ui/types/cms";

export interface CardBlockProps {
  item: CardBlockType;
}

export default function CardBlock({ item }: Readonly<CardBlockProps>) {
  // Determine alignment from CMS, default to "center"
  const alignment = item?.contentalignment?.[0]?.codename || "center";

  const alignmentClass =
    alignment === "left"
      ? "items-start text-left"
      : alignment === "right"
        ? "items-end text-right"
        : "items-center text-center";

  return (
    <Card
      className="flex flex-col h-full p-0 transition-all duration-200 shadow-sm hover:shadow-md"
      data-testid="card-block-card"
    >
      <CardContent
        className={`flex flex-1 flex-col pt-4 ${alignmentClass}`}
        data-testid="card-block-content"
      >
        {item?.image && (
          <div className="relative w-10 h-10 p-2" data-testid="card-block-image-wrapper">
            <Image
              src={item.image}
              alt={item.title || "icon"}
              fill
              className="object-contain"
              data-testid="card-block-image"
            />
          </div>
        )}

        {item?.title && (
          <h3
            className="text-lg font-semibold text-brand-blue [&>p]:m-0"
            dangerouslySetInnerHTML={{ __html: item.title }}
            data-testid="card-block-title-lbl"
          />
        )}

        {item?.description && (
          <p
            className="mt-1 text-black [&>p]:m-0"
            dangerouslySetInnerHTML={{ __html: item.description }}
            data-testid="card-block-description-lbl"
          />
        )}
      </CardContent>

      {item?.link && item?.link_text && (
        <CardFooter
          className={`mx-6 flex border-t border-gray-200 !pt-2 pb-2 ${
            alignment === "left"
              ? "justify-start"
              : alignment === "right"
                ? "justify-end"
                : "justify-center"
          }`}
          data-testid="card-block-footer"
        >
          <Link href={item.link} target="_blank" data-testid="card-block-link">
            <button
              className="font-medium cursor-pointer text-brand-cyan hover:underline hover:text-brand-blue-50"
              data-testid="card-block-btn"
            >
              {item.link_text}
              <ChevronRight className="inline-block ml-1" size={16} />
            </button>
          </Link>
        </CardFooter>
      )}
    </Card>
  );
}
